import React, { useState, useEffect } from "react"
import * as uuid from "uuid"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as brands from "brands"
import * as api from "brandguard/api"
import * as bgdocuments from "brandguard/display/brandguard/documents"
import * as bglayouts from "brandguard/components/layouts"
import * as bgapprovals from "brandguard/display/approval"
import * as authz from "authz"
import { useSettings } from "accounts/settings"
import { State } from "accounts/api"
import classnames from "classnames"
import * as scores from "brandguard/components/layouts/scores"
import * as debugx from "x/debugx"
import * as icons from "icons"
import JSZip from "jszip"
import * as media from "media"
import * as bgscores from "brandguard/display/scores"
import * as typography from "typography"

export default function Display(props: React.PropsWithChildren<unknown>): JSX.Element {
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const permission = authzaccount.current
	const settings = useSettings()
	const bearertoken = sessions.useToken()
	const brand = brands.caching.useCached()
	const mtoggle = layouts.modals.useToggle()

	const [loading, setLoading] = useState(true)
	const [selectedItem, setSelectedItem] = useState(undefined as api.DocumentSearchResponseItem | undefined)
	const [checkedItems, setCheckedItems] = useState([] as api.DocumentSearchResponseItem[])
	const [filterFocused, setFilterFocused] = useState(false)
	const [currentPage, setCurrentPage] = useState<api.DocumentPage | undefined>(undefined)

	const _searchReq = api.documents.uploads.searches.zero({
		offset: 0n,
		brand_id: brand.id,
		limit: 20n,
		observation: [api.Prediction.APPROVED, api.Prediction.APPROVED_BY_SYSTEM],
	})

	const [searchReq, setSearchReq] = useState(_searchReq)
	const [cursorKey, setCursorKey] = useState(uuid.v4())

	const [documents, setDocuments] = useState({
		items: [],
		next: searchReq,
	} as api.DocumentSearchResponse)

	useEffect(() => {
		if (searchReq.brand_id === uuid.NIL) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.documents.approvallog.search(searchReq.brand_id, searchReq, bearertoken))
			.then(setDocuments)
			.catch(httpx.errors.cancellation(console.warn))
			.catch((cause) => console.error("unable to load brandguard approved documents", cause))
			.finally(() => {
				setLoading(false)
			})
		return p.cancel
	}, [searchReq])

	useEffect(() => {
		if (!selectedItem) {
			setCurrentPage(undefined)
			return
		}
		setCurrentPage(selectedItem.pages?.[0])
	}, [selectedItem?.media?.id])

	useEffect(() => {
		if (!selectedItem) return
		mtoggle(
			<layouts.Flex styled flexDirection="column">
				<bgapprovals.DocumentPreview
					item={selectedItem}
					flex="1"
					onClose={() => {
						mtoggle(undefined)
						setSelectedItem(undefined)
					}}
					onPageChange={(_index, page) => setCurrentPage(page)}
				>
					<typography.h3 fontWeight="700" fontSize="18px" lineHeight="30px" color="#4D5E80">
						Asset Scores
					</typography.h3>
					<bgdocuments.Scores item={selectedItem} currentPage={currentPage} />
					<bgapprovals.status.default>
						<bgapprovals.status.ApprovalLog item={selectedItem} borderRadius="10px" />
					</bgapprovals.status.default>
					<authz.Protected enabled={debugx.alpha.enabled()}>
						<bgscores.layouts.accordion.Container title="Debug Info" textAlign="initial">
							<bgdocuments.Debug item={selectedItem} />
						</bgscores.layouts.accordion.Container>
					</authz.Protected>
					<layouts.containers.flex className="actions" flex="1" alignItems="flex-end">
						<authz.Protected enabled={permission.brandguard_upload}>
							<bgdocuments.MultiActions uploadItems={[selectedItem]} historicalItems={[]} />
						</authz.Protected>
					</layouts.containers.flex>
				</bgapprovals.DocumentPreview>
			</layouts.Flex>,
		).then((e) => setSelectedItem(undefined))
	}, [selectedItem, currentPage])

	const onDocumentUpdate = (item: api.DocumentSearchResponseItem): void => {
		setDocuments((prevState) => ({
			...prevState,
			items: prevState.items.map((i) => (i.media?.id === item.media?.id ? item : i)),
		}))
	}

	const _allselected = documents.items.length > 0 && checkedItems.length === documents.items.length

	const handleSelectAll = (): void => {
		if (_allselected) {
			setCheckedItems([])
			return
		}
		setCheckedItems(documents.items)
	}

	const fcnt = bgapprovals.CalculateFilterSCount(_searchReq, searchReq)

	return (
		<layouts.containers.flex flexDirection="column-reverse" overflow="hidden">
			<layouts.pagination.Cursor
				key={cursorKey}
				my="auto"
				justifyContent="center"
				current={searchReq.offset}
				advance={Number(documents.next!.offset) === -1 ? undefined : documents.next?.offset}
				onChange={(next) => {
					setSearchReq({
						...searchReq,
						offset: next,
					})
				}}
			/>
			<layouts.loading.screen
				className="brandguard-working-area"
				loading={loading}
				flex="1"
				overflow="hidden"
				icon={<></>}
				flexDirection="row"
			>
				<layouts.containers.flex className="center-panel" flex="6" flexDirection="column">
					<layouts.containers.flex flexDirection="column" flex="1" minHeight="100px">
						<bglayouts.OverlaysContainer overflow="auto" background={layouts.theme.backgrounds.whitealpha80}>
							<layouts.containers.ResponsiveGrid gap={20} p="25px">
								{documents.items.map((item) => {
									return (
										<bgapprovals.documents.CardDisplay
											key={item.media?.id}
											item={item}
											active={selectedItem && item?.media?.id === selectedItem.media?.id}
											onCardClick={() => {
												setSelectedItem(item)
											}}
											onChange={onDocumentUpdate}
											onChecked={(checked) =>
												setCheckedItems((prevState) =>
													checked ? [...prevState, item] : prevState.filter((i) => i.media?.id !== item.media?.id),
												)
											}
											score={<></>}
											panel={
												<bgapprovals.status.default>
													<bgapprovals.status.ApprovalLog item={item} borderRadius="0 0 10px 10px" />
												</bgapprovals.status.default>
											}
											find={api.documents.uploads.find}
											checked={checkedItems.some((i) => i.media?.id === item.media?.id)}
										/>
									)
								})}
							</layouts.containers.ResponsiveGrid>
							<layouts.loading.pending loading={loading || documents.next?.offset === searchReq.offset}>
								{documents.items.length === 0 && <bgapprovals.layouts.ZeroState />}
							</layouts.loading.pending>
						</bglayouts.OverlaysContainer>
					</layouts.containers.flex>
				</layouts.containers.flex>
			</layouts.loading.screen>
			<layouts.containers.flex flexDirection="row" className={classnames(bgapprovals.layouts.styledactions)}>
				<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative">
					<bgapprovals.layouts.FilterButton
						width="145px"
						height="40px"
						borderRadius="4px"
						onClick={() => setFilterFocused(!filterFocused)}
						className={fcnt > 0 ? "active" : ""}
					>
						<layouts.containers.flex flexDirection="row" justifyContent="center">
							<layouts.containers.box>
								<icons.bgapproval.filter />
							</layouts.containers.box>
							<layouts.containers.box px="10px">Filter{fcnt > 0 ? `/${fcnt}` : ""}</layouts.containers.box>
							<layouts.containers.box>
								{filterFocused ? <icons.bgapproval.arrowDown /> : <icons.bgapproval.arrowDown />}
							</layouts.containers.box>
						</layouts.containers.flex>
					</bgapprovals.layouts.FilterButton>
					<layouts.containers.flex display={filterFocused ? "flex" : "none"} position="absolute" top="100%" pt="5px">
						<bgapprovals.FilterDisplay
							onFilterClear={() => {
								setSearchReq(_searchReq)
								setCursorKey(uuid.v4())
							}}
							_searchReq={_searchReq}
							onFilterChange={(r) => {
								setSearchReq({ ...searchReq, ...r, ...{ offset: 0n } })
								setCursorKey(uuid.v4())
							}}
						/>
					</layouts.containers.flex>
				</layouts.containers.flex>
				<layouts.containers.flex className="actions" justifyContent="flex-end" flex="10">
					<bgapprovals.layouts.SelectAll
						disabled={documents.items.length === 0}
						cursor="pointer"
						className="select-all-action"
						onClick={handleSelectAll}
					>
						{_allselected ? "Unselect All" : "Select All"}
					</bgapprovals.layouts.SelectAll>
					<authz.Protected
						enabled={permission.brandguard_upload && checkedItems.length > 0}
						rejected={
							<layouts.containers.flex className="download-action">
								<icons.brandguard.download />
							</layouts.containers.flex>
						}
					>
						<layouts.containers.flex
							className="download-action"
							cursor="pointer"
							onClick={() => {
								const zip = new JSZip()
								const promises: Promise<api.DocumentPatchRequest | JSZip>[] = []
								checkedItems.forEach((i) => {
									promises.push(
										media.cache.current(authzaccount.current.id, i.media!.md5, bearertoken).then((url) => {
											return fetch(url)
												.then((response) => {
													return response.arrayBuffer()
												})
												.then((documentBuffer) => {
													const documentName = media.downloads.filename(i.media!.md5, i.media!.mimetype)
													return zip.file(documentName, documentBuffer)
												})
										}),
									)
								})
								Promise.all(promises).then(() => {
									zip.generateAsync({ type: "blob" }).then((content) => {
										const downloadLink = document.createElement("a")
										downloadLink.href = URL.createObjectURL(content)
										downloadLink.download = "brandguard-documents.zip"
										downloadLink.click()
									})
								})
							}}
						>
							<icons.brandguard.download />
						</layouts.containers.flex>
					</authz.Protected>
				</layouts.containers.flex>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}
